import type {
	FetchShortsOptions,
	ShortVideo
} from '~/composables/shorts/useFetchShorts';
import type { ShortsFilters } from '~/core/entities/property/filters';

import { useFetchShorts } from '~/composables/shorts/useFetchShorts';
import { useShortsFeedStore } from '~/store/shortsFeedStore';

interface FeedSeedOptions {
	currentPage: number;
	perPage?: number;
	hasMore: boolean;
}

export function useFeedState() {
	const store = useShortsFeedStore();
	const { fetchVideos } = useFetchShorts();
	const isPreloading = ref(false);

	async function fetchFeed(
		filters?: ShortsFilters,
		options: FetchShortsOptions = {}
	): Promise<void> {
		const capturedId = store.guardNext();
		store.isLoading = true;
		store.activeFilters = filters ?? {};
		store.feedPerPage = undefined;

		try {
			const result = await fetchVideos(filters ?? {}, 1, undefined, options);
			if (store.guardIsStale(capturedId)) return;
			store.setVideos(result.items);
			store.activeFilters = result.appliedFilters;
			store.hasMore = result.hasMore;
			store.allWatched = result.allWatched ?? false;
			store.totalAvailable = result.totalAvailable ?? 0;
			store.recommendationCountVideos = result.recommendationCountVideos;
			store.recommendationCountPhotos = result.recommendationCountPhotos;
			store.currentPage = 1;
			store.isPreloaded = true;
		} finally {
			if (!store.guardIsStale(capturedId)) {
				store.isLoading = false;
			}
		}
	}

	async function loadMore(): Promise<void> {
		if (
			isPreloading.value ||
			store.isLoading ||
			!store.hasMore ||
			!Object.keys(store.activeFilters).length
		)
			return;

		const capturedId = store.guardNext();
		store.isLoading = true;

		try {
			const nextPage = store.currentPage + 1;

			const result = await fetchVideos(
				store.activeFilters,
				nextPage,
				store.feedPerPage
			);
			if (store.guardIsStale(capturedId)) return;
			store.appendVideos(result.items);
			store.activeFilters = result.appliedFilters;
			store.hasMore = result.hasMore;
			store.allWatched = result.allWatched ?? store.allWatched;
			store.totalAvailable = result.totalAvailable ?? store.totalAvailable;
			store.recommendationCountVideos =
				result.recommendationCountVideos ?? store.recommendationCountVideos;
			store.recommendationCountPhotos =
				result.recommendationCountPhotos ?? store.recommendationCountPhotos;
			store.currentPage = nextPage;
		} finally {
			if (!store.guardIsStale(capturedId)) {
				store.isLoading = false;
			}
		}
	}

	async function applyFilters(
		filters: ShortsFilters,
		options: FetchShortsOptions = {}
	): Promise<void> {
		store.guardNext();
		store.reset();
		store.activeFilters = filters;
		store.isLoading = true;

		await fetchFeed(filters, options);
	}

	function reset(): void {
		store.reset();
	}

	/**
	 * Popula o feed com itens já carregados (ex: restantes do carrossel de origem),
	 * sem fazer chamada à API. O `loadMore` subsequente busca a próxima página normalmente.
	 */
	function seedFromCarousel(
		items: ShortVideo[],
		activeFilters: ShortsFilters,
		options: FeedSeedOptions
	): void {
		isPreloading.value = true;
		store.reset();
		store.setVideos(items);
		store.activeFilters = activeFilters;
		store.currentPage = options.currentPage;
		store.feedPerPage = options.perPage;
		store.hasMore = options.hasMore;
		store.isPreloaded = true;
		isPreloading.value = false;
	}

	return { fetchFeed, loadMore, applyFilters, reset, seedFromCarousel };
}
